import type { AeropayBankName, PaymentStatusValue } from '~/common/enums/payments.enum';
import type { VoucherResponseDto } from '~/domains/promotions/types/voucher.types';

/**
 * Paysafe payment-handle state, which is a narrower domain than a transaction's
 * PaymentStatus — a handle is never PENDING, RECEIVED, ERROR or CANCELLED. Keep the two
 * apart: PAYABLE here means "handle ready to charge", not "funds in flight".
 */
const enum PaymentHandleStatus {
  INITIATED = 'INITIATED',
  COMPLETED = 'COMPLETED',
  PAYABLE = 'PAYABLE',
  PROCESSING = 'PROCESSING',
  EXPIRED = 'EXPIRED',
  FAILED = 'FAILED',
}

/** String-union form of {@link PaymentHandleStatus}, for typing API payload fields. */
type PaymentHandleStatusValue = `${PaymentHandleStatus}`;

const enum PaymentType {
  CARD = 'CARD',
  PAYPAL = 'PAYPAL',
  VIPPREFERRED = 'VIPPREFERRED',
  VENMO = 'VENMO',
  AEROPAY = 'AEROPAY',
}

const enum CardType {
  VI = 'VI',
  MC = 'MC',
  AM = 'AM',
  DI = 'DI',
}

const enum CardCategory {
  CREDIT = 'CREDIT',
  DEBIT = 'DEBIT',
}

interface CardExpiryDto {
  month: number;
  year: number;
}

interface ICardResponseDto {
  lastDigits: string;
  cardExpiry: CardExpiryDto;
  cardBin: string;
  cardType: CardType;
  cardCategory: CardCategory;
  holderName: string;
}

interface IPaymentHandleDto {
  id: string;
  merchantRefNum: string;
  status: PaymentHandleStatusValue;
  paymentType: PaymentType;
  accountId: string;
  card: ICardResponseDto;
  billingDetailsId: string;
}

interface IPaymentHandlesResponseDto {
  id: string;
  status: string;
  merchantCustomerId: string;
  paymentToken: string;
  paymentHandles: IPaymentHandleDto[];
}

interface IGetWalletsPaymentHandles {
  id: string;
  data: IPaymentHandlesResponseDto;
}

interface IGetWalletsBalanceResponseDto {
  availableBalance: number; // This is in dollars (not cents)
  credits: number;
  withdrawableBalance: number;
  refundableBalance: number;
  playthroughCash?: number;
}

interface IGetWalletsBalance {
  id: string;
  data: IGetWalletsBalanceResponseDto;
}

interface IRefundableBreakdownItem {
  paymentMethod: string;
  refundableBalance: number;
}

interface IGetWalletsRefundableBreakdownResponseDto {
  items: IRefundableBreakdownItem[];
}

interface IGetWalletsRefundableBreakdown {
  id: string;
  data: IGetWalletsRefundableBreakdownResponseDto;
}

interface IPostWalletsPaymentsHandlesPaypalPayload {
  merchantRefNum: string;
  email?: string;
  amount: number;
  transactionType: 'PAYMENT';
  currency: 'USD';
  savePaymentMethod?: boolean;
  simulator: 'INTERNAL' | 'EXTERNAL';
  returnLinks?: {
    rel: 'default' | 'on_failed' | 'on_completed';
    href: string;
    method: 'GET' | 'POST';
  }[];
}

interface IPostWalletsPaymentsHandlesPaypalResponse {
  id: string;
  data: {
    id: string;
    status: PaymentHandleStatusValue;
    paymentHandleToken: string;
    links: {
      rel: 'redirect_payment';
      href: string;
    }[];
  };
}

interface IPostWalletsDepositsPayload {
  paymentHandleId?: string;
  cvv?: string;
  paymentHandleToken?: string;
  amount: number;
  currency: 'USD';
  merchantRefNum: string;
  savePaymentMethod?: boolean;
  paymentType?: PaymentType;
  bonusId?: string;
  returnLinks?: {
    rel: 'default' | 'on_failed' | 'on_completed';
    href: string;
    method: 'GET' | 'POST';
  }[];
}

interface IPostWalletsDepositsResponseData {
  userId: string;
  status: PaymentStatusValue;
  bonusCreditsAssigned?: number;
  addedToWalletAmount?: number;
}

interface IPostWalletsDepositsResponse {
  id: string;
  data: IPostWalletsDepositsResponseData;
}

export type VIPPNativeQueryParams = {
  amountInCents: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dobDay: string;
  dobMonth: string;
  dobYear: string;
  address: string;
  city: string;
  zip: string;
  state: string;
  country: string;
};

interface IPostWalletsPaymentsHandlesVIPPPayload {
  merchantRefNum: string;
  amount?: number;
  returnLinks?: {
    rel: 'default';
    href: string;
    method: 'POST';
  }[];
  query?: VIPPNativeQueryParams;
}

export type BankAccount = {
  bankAccountId: string;
  userId: string;
  bankName: AeropayBankName;
  accountLast4: string;
  name: string;
  externalBankAccountId: string;
  accountType: string;
  status: string;
};

interface IPostWalletsRefundsPayload {
  paymentMethod: string;
  amount: number;
}

interface IPostWalletsRefundsResponse {
  id: string;
  status: string;
  message?: string;
}

interface IGetVouchersResponse {
  data: VoucherResponseDto[];
  totalCount?: number;
  page?: number;
  pageSize?: number;
}

export {
  CardCategory,
  CardType,
  PaymentType,
  type IGetWalletsBalance,
  type IGetWalletsPaymentHandles,
  type IGetWalletsRefundableBreakdown,
  type IRefundableBreakdownItem,
  type IPostWalletsDepositsPayload,
  type IPostWalletsDepositsResponse,
  type IPostWalletsDepositsResponseData,
  type IPostWalletsPaymentsHandlesPaypalPayload,
  type IPostWalletsPaymentsHandlesPaypalResponse,
  type IPostWalletsPaymentsHandlesVIPPPayload,
  type IPostWalletsRefundsPayload,
  type IPostWalletsRefundsResponse,
  type IGetVouchersResponse,
};
